import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_AUDIT_TRAIL } from "../../store/details";
import { LdsTable, LdsBadge } from "@elilillyco/ux-lds-react";
import CLoader from "../shared/CLoader";

const FIELD_LABELS = {
  list_status:      "Status",
  list_title:       "Title",
  specialties:      "Specialty list",
  department_field: "Department",
  brand_nm:         "Brand",
  indication_nm:    "Indication",
  thpc_nm:          "THPC",
  dsst_nm:          "Disease State",
  mdpr_nm:          "Molecule",
  list_quarter:     "Quarter",
};

const STATUS_BADGE_MAP = {
  DRAFT:                  "neutral",
  "REQUEST FOR REVIEW":   "warning",
  "REQUEST FOR APPROVAL": "warning",
  APPROVED:               "success",
  ARCHIVE:                "neutral",
};

const getEditorName = (edited_by) => {
  if (!edited_by) return "Unknown";
  if (typeof edited_by === "object") {
    const { first_name, last_name, email_id } = edited_by;
    if (first_name || last_name) return `${first_name || ""} ${last_name || ""}`.trim();
    return email_id || "Unknown";
  }
  return edited_by;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const formatValue = (val) => {
  if (val === null || val === undefined || val === "") return <em>empty</em>;
  return String(val);
};

/**
 * Renders the specialty diff sub-section.
 * diff = { added: [...], removed: [...], rc_changed: [...] }
 */
const SpecialtyDiff = ({ diff }) => {
  if (!diff) return <em>no changes</em>;
  const { added = [], removed = [], rc_changed = [] } = diff;

  // Format added/removed items (handles both old string format and new object format)
  const formatSpecialtyItem = (item) => {
    if (typeof item === "object" && item.specialty_code) {
      return item.specialty_description
        ? `${item.specialty_code} - ${item.specialty_description}`
        : item.specialty_code;
    }
    return String(item);
  };

  return (
    <div>
      {added.length > 0 && (
        <div style={{ marginBottom: "6px" }}>
          <span style={{ color: "#198754", fontWeight: 600 }}>Specialties Added:</span>{" "}
          {added.map(formatSpecialtyItem).join(", ")}
        </div>
      )}
      {removed.length > 0 && (
        <div style={{ marginBottom: "6px" }}>
          <span style={{ color: "#dc3545", fontWeight: 600 }}>Specialties Removed:</span>{" "}
          {removed.map(formatSpecialtyItem).join(", ")}
        </div>
      )}
      {rc_changed.length > 0 && (
        <div>
          <span style={{ fontWeight: 600 }}>RC / Incentivized Changes:</span>
          <ul style={{ margin: "4px 0 0 0", paddingLeft: "1.2rem" }}>
            {rc_changed.map((c) => (
              <li key={c.specialty_code} style={{ marginBottom: "4px" }}>
                <strong>
                  {c.specialty_code}
                  {c.specialty_description ? ` - ${c.specialty_description}` : ""}
                </strong>
                {c.rc_added && c.rc_added.length > 0 && (
                  <div style={{ marginLeft: "8px" }}>
                    <span style={{ color: "#198754", fontWeight: 500 }}>RC Added:</span>{" "}
                    {c.rc_added.join(", ")}
                  </div>
                )}
                {c.rc_removed && c.rc_removed.length > 0 && (
                  <div style={{ marginLeft: "8px" }}>
                    <span style={{ color: "#dc3545", fontWeight: 500 }}>RC Removed:</span>{" "}
                    {c.rc_removed.join(", ")}
                  </div>
                )}
                {/* Show old_rc → new_rc when rc_added/rc_removed are empty but RC values differ */}
                {(!c.rc_added || c.rc_added.length === 0) && (!c.rc_removed || c.rc_removed.length === 0) && c.old_rc !== c.new_rc && (c.old_rc || c.new_rc) && (
                  <div style={{ marginLeft: "8px" }}>
                    <span style={{ fontWeight: 500 }}>RC:</span>{" "}
                    <em>{c.old_rc || "none"}</em> → <em>{c.new_rc || "none"}</em>
                  </div>
                )}
                {c.incentivized_changed && (
                  <div style={{ marginLeft: "8px" }}>
                    <span style={{ fontWeight: 500 }}>Incentivized:</span>{" "}
                    {String(c.old_incentivized || false)} → {String(c.new_incentivized || false)}
                  </div>
                )}
                {/* Backward compatibility: handle old format */}
                {!c.incentivized_changed && c.old_incentivized !== c.new_incentivized && (
                  <div style={{ marginLeft: "8px" }}>
                    <span style={{ fontWeight: 500 }}>Incentivized:</span>{" "}
                    {String(c.old_incentivized)} → {String(c.new_incentivized)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const EVENT_LABELS = {
  lock_conflict_attempt: { label: "Edit Blocked (List Locked)", color: "#856404", bg: "#fff3cd" },
  force_unlock:          { label: "Force Unlock by Admin",      color: "#842029", bg: "#f8d7da" },
};

/**
 * Renders the "What Changed" cell, handling special event types and normal field diffs.
 */
const ChangesCell = ({ entry }) => {
  const eventMeta = EVENT_LABELS[entry.event_type];
  if (eventMeta) {
    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: "4px",
          background: eventMeta.bg,
          color: eventMeta.color,
          fontWeight: 600,
          fontSize: "0.85em",
        }}
      >
        {eventMeta.label}
        {entry.event_type === "force_unlock" && entry.after?.unlock_reason && (
          <span style={{ fontWeight: 400 }}>: "{entry.after.unlock_reason}"</span>
        )}
      </span>
    );
  }

  const fields = entry.changed_fields || [];
  const before = entry.before || {};
  const after  = entry.after  || {};

  if (fields.length === 0) return <span>—</span>;

  return (
    <ul style={{ margin: 0, paddingLeft: "1rem" }}>
      {fields.map((f) => (
        <li key={f}>
          <strong>{FIELD_LABELS[f] || f}:</strong>{" "}
          {f === "specialties" ? (
            <SpecialtyDiff diff={after[f]} />
          ) : (
            <span>{formatValue(before[f])} → {formatValue(after[f])}</span>
          )}
        </li>
      ))}
    </ul>
  );
};

const AuditTrail = ({ listUUID }) => {
  const dispatch = useDispatch();
  const { auditTrail, auditLoading } = useSelector(({ details }) => details);

  useEffect(() => {
    if (listUUID) {
      dispatch(FETCH_AUDIT_TRAIL(listUUID));
    }
  }, [dispatch, listUUID]);

  return (
    <div className="content-block mt-4">
      <h4>Audit Trail</h4>
      {auditLoading ? (
        <CLoader size={60} />
      ) : auditTrail.length === 0 ? (
        <p className="text-muted">No edit history available for this list.</p>
      ) : (
        <LdsTable>
          <thead>
            <tr>
              <th style={{ textAlign: "center" }}>Date &amp; Time</th>
              <th style={{ textAlign: "center" }}>Edited By</th>
              <th style={{ textAlign: "center" }}>New Status</th>
              <th style={{ textAlign: "center" }}>What Changed</th>
            </tr>
          </thead>
          <tbody>
            {auditTrail.map((entry) => (
              <tr key={entry.audit_id}>
                <td style={{ whiteSpace: "nowrap" }}>{formatDate(entry.edited_date)}</td>
                <td style={{ whiteSpace: "nowrap" }}>{getEditorName(entry.edited_by)}</td>
                <td>
                  {entry.list_status_new ? (
                    <LdsBadge classes={STATUS_BADGE_MAP[entry.list_status_new] || "neutral"}>
                      {entry.list_status_new}
                    </LdsBadge>
                  ) : "—"}
                </td>
                <td style={{ textAlign: "left" }}><ChangesCell entry={entry} /></td>
              </tr>
            ))}
          </tbody>
        </LdsTable>
      )}
    </div>
  );
};

export default AuditTrail;
